import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  PortalEventContextType,
  PortalInviteStatusDto,
  PortalAwardOutcomeDto,
  PortalLifecycleStatus,
  useGetPortalNegotiationRoundsQuery,
  type PortalNegotiationRoundDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useAppSelector } from "../../../../common/redux/hooks";
import { roundExit, type RoundExit } from "./missedPortalRound";
import { roundAcceptsBids } from "./roundAcceptsBids";

const isParticipating = (event: PortalSourcingEventDetailDto): boolean =>
  event.myInviteStatus != null &&
  event.myInviteStatus !== PortalInviteStatusDto.NotInvited &&
  event.myInviteStatus !== PortalInviteStatusDto.Declined;

export const portalEventMayHaveRounds = (event: PortalSourcingEventDetailDto): boolean => {
  const isPrivate = event.contextType === PortalEventContextType.Private;
  const isActive = event.lifecycle.status !== PortalLifecycleStatus.Cancelled;
  return isActive && (!isPrivate || isParticipating(event));
};

export const isOutcomePending = (
  event: PortalSourcingEventDetailDto,
  ownAwardOutcome?: PortalAwardOutcomeDto | null,
): boolean =>
  (event.lifecycle.status === PortalLifecycleStatus.DeadlinePassed ||
    event.lifecycle.status === PortalLifecycleStatus.UnderEvaluation) &&
  ownAwardOutcome !== PortalAwardOutcomeDto.Winner &&
  ownAwardOutcome !== PortalAwardOutcomeDto.NotSelected;

export interface OpenPortalRound {
  round: PortalNegotiationRoundDto | null;
  exit: RoundExit | null;
  isLoading: boolean;
  isError: boolean;
}

export const useOpenPortalRound = (
  event: PortalSourcingEventDetailDto,
  ownAwardOutcome?: PortalAwardOutcomeDto | null,
): OpenPortalRound => {
  const authState = useAppSelector((state) => state.authState.state);
  const relevant = authState?.type === "LoggedInWithOrgContext" && portalEventMayHaveRounds(event);
  const { data, isLoading, isError } = useGetPortalNegotiationRoundsQuery(relevant ? { eventId: event.id } : skipToken);
  const pending = isOutcomePending(event, ownAwardOutcome);
  return {
    round:
      (data?.rounds ?? [])
        .filter((round) => roundAcceptsBids(round) && round.isCallerInvited)
        .sort((a, b) => a.roundNumber - b.roundNumber)
        .at(-1) ?? null,
    exit: pending ? roundExit(data?.rounds ?? []) : null,
    isLoading: relevant && pending && isLoading,
    isError: relevant && pending && isError,
  };
};
